import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ArrowUpRight, Check, LoaderCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/generals/button-link'
import { BUDGETS, CONTACT_LIMITS } from '@/constants/contact'

type FieldName = 'name' | 'email' | 'company' | 'budget' | 'message'

type FormValues = Record<FieldName | 'website', string>

type FieldErrors = Partial<Record<FieldName, string>>

type Status = 'idle' | 'submitting' | 'success' | 'error'

type ContactResponse = {
    ok?: boolean
    message?: string
    fields?: FieldErrors
}

const EMPTY_VALUES: FormValues = {
    name: '',
    email: '',
    company: '',
    budget: '',
    message: '',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const FIELD_ORDER: Array<FieldName> = ['name', 'email', 'company', 'budget', 'message']

// How long the "message sent" confirmation stays on screen, in milliseconds.
const CONFIRMATION_MS = 6000

const LABEL = 'font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground'

const inputClasses = (invalid: boolean): string => cn(
    'w-full rounded-none border-0 border-b bg-transparent px-0 py-3 text-lg text-ink outline-none transition-[border-color,box-shadow] placeholder:text-ink/25 focus:border-ink focus:shadow-[0_1px_0_0_var(--ink)]',
    invalid ? 'border-destructive' : 'border-ink/20'
)

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}

    if (values.name.trim().length < 2) errors.name = "Please tell us your name."
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email address."
    if (values.message.trim().length < CONTACT_LIMITS.minMessage) errors.message = "Please add a few words about your project."

    return errors
}

const focusFirstError = (errors: FieldErrors): void => {
    const first = FIELD_ORDER.find(field => errors[field])

    if (first) document.getElementById(`contact-${first}`)?.focus()
}

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(EMPTY_VALUES)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [firstName, setFirstName] = useState('')
    const [confirmation, setConfirmation] = useState(0)

    // Hide the confirmation on its own after a few seconds. A new submission restarts the timer.
    useEffect(() => {
        if (!confirmation) return

        const timer = window.setTimeout(() => setConfirmation(0), CONFIRMATION_MS)

        return () => window.clearTimeout(timer)
    }, [confirmation])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (field !== 'website' && errors[field]) {
            setErrors(current => ({ ...current, [field]: undefined }))
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validate(values)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            focusFirstError(found)
            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = (await response.json().catch(() => null)) as ContactResponse | null

            if (!response.ok || !data?.ok) {
                const fields = data?.fields ?? {}

                setErrors(fields)
                setServerMessage(data?.message ?? "Something went wrong on our side. Please try again in a moment.")
                setStatus('error')
                focusFirstError(fields)
                return
            }

            setFirstName(values.name.trim().split(/\s+/)[0] ?? '')
            setValues(EMPTY_VALUES)
            setStatus('success')
            setConfirmation(Date.now())
        }
        catch {
            setServerMessage("We could not reach the server. Check your connection and try again.")
            setStatus('error')
        }
    }

    const submitting = status === 'submitting'

    return (
        <>
        <div role='status' aria-live='polite' className='pointer-events-none fixed inset-x-4 bottom-4 z-50 flex justify-center sm:inset-x-auto sm:right-6 sm:bottom-6'>
            {confirmation > 0 && (
                <div
                    key={confirmation}
                    className='pointer-events-auto relative flex w-full max-w-sm items-start gap-4 overflow-hidden border border-line bg-white p-5 pr-12 shadow-[0_18px_40px_-20px_rgba(10,11,13,0.35)] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 duration-500'
                >
                    <span className='inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-azure-500 text-white'>
                        <Check aria-hidden='true' className='size-5' />
                    </span>

                    <div>
                        <p className='text-base font-medium tracking-[-0.01em] text-ink'>
                            Message sent{firstName ? `, thank you ${firstName}` : ", thank you"}.
                        </p>
                        <p className='mt-1 text-sm leading-relaxed text-ink/65'>
                            A partner will reply within one business day.
                        </p>
                    </div>

                    <button
                        type='button'
                        onClick={() => setConfirmation(0)}
                        aria-label="Dismiss confirmation"
                        className='absolute top-3 right-3 inline-flex size-8 items-center justify-center rounded-full text-ink/50 transition-colors hover:bg-mist hover:text-ink focus-visible:outline-2 focus-visible:outline-azure-500'
                    >
                        <X aria-hidden='true' className='size-4' />
                    </button>

                    <span
                        aria-hidden='true'
                        className='confirmation-timer absolute inset-x-0 bottom-0 h-0.5 origin-left bg-azure-500'
                        style={{ animationDuration: `${CONFIRMATION_MS}ms` }}
                    />
                </div>
            )}
        </div>

        <form noValidate onSubmit={onSubmit} className='flex flex-col gap-10' aria-describedby='contact-note'>
            <div className='grid gap-10 md:grid-cols-2 md:gap-8'>
                <div className='flex flex-col gap-1'>
                    <label htmlFor='contact-name' className={LABEL}>Your name</label>
                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        maxLength={CONTACT_LIMITS.name}
                        value={values.name}
                        onChange={update('name')}
                        placeholder="Giulia Rossi"
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                        className={inputClasses(Boolean(errors.name))}
                    />
                    {errors.name && <p id='contact-name-error' className='mt-1 text-sm text-destructive'>{errors.name}</p>}
                </div>

                <div className='flex flex-col gap-1'>
                    <label htmlFor='contact-email' className={LABEL}>Work email</label>
                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        maxLength={CONTACT_LIMITS.email}
                        value={values.email}
                        onChange={update('email')}
                        placeholder="giulia@company.com"
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'contact-email-error' : undefined}
                        className={inputClasses(Boolean(errors.email))}
                    />
                    {errors.email && <p id='contact-email-error' className='mt-1 text-sm text-destructive'>{errors.email}</p>}
                </div>
            </div>

            <div className='flex flex-col gap-1'>
                <label htmlFor='contact-company' className={LABEL}>Company (optional)</label>
                <input
                    id='contact-company'
                    name='company'
                    type='text'
                    autoComplete='organization'
                    maxLength={CONTACT_LIMITS.company}
                    value={values.company}
                    onChange={update('company')}
                    placeholder="Where you work"
                    aria-invalid={Boolean(errors.company)}
                    aria-describedby={errors.company ? 'contact-company-error' : undefined}
                    className={inputClasses(Boolean(errors.company))}
                />
                {errors.company && <p id='contact-company-error' className='mt-1 text-sm text-destructive'>{errors.company}</p>}
            </div>

            <fieldset>
                <legend className={LABEL}>Budget (optional)</legend>

                <div className='mt-4 flex flex-wrap gap-2'>
                    {BUDGETS.map((budget, index) => (
                        <label key={budget} className='relative cursor-pointer'>
                            <input
                                id={index === 0 ? 'contact-budget' : undefined}
                                type='radio'
                                name='budget'
                                value={budget}
                                checked={values.budget === budget}
                                onChange={update('budget')}
                                className='peer sr-only'
                            />
                            <span className='inline-flex h-10 items-center rounded-full border border-ink/15 px-4 text-sm text-ink/80 transition-colors hover:border-ink peer-checked:border-ink peer-checked:bg-ink peer-checked:text-white peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-azure-500'>
                                {budget}
                            </span>
                        </label>
                    ))}
                </div>

                {errors.budget && <p className='mt-2 text-sm text-destructive'>{errors.budget}</p>}
            </fieldset>

            <div className='flex flex-col gap-1'>
                <label htmlFor='contact-message' className={LABEL}>About your project</label>
                <textarea
                    id='contact-message'
                    name='message'
                    rows={5}
                    maxLength={CONTACT_LIMITS.message}
                    value={values.message}
                    onChange={update('message')}
                    placeholder="What are you building, and when would you like to launch?"
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    className={cn(inputClasses(Boolean(errors.message)), 'resize-y')}
                />
                {errors.message && <p id='contact-message-error' className='mt-1 text-sm text-destructive'>{errors.message}</p>}
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='contact-website'>Leave this field empty</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {status === 'error' && serverMessage && (
                <p role='alert' className='border-l-2 border-destructive pl-4 text-sm text-destructive'>
                    {serverMessage}
                </p>
            )}

            <div className='flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between'>
                <button
                    type='submit'
                    disabled={submitting}
                    className={cn(buttonClasses('primary'), 'h-14 px-8 text-base disabled:cursor-wait disabled:opacity-70')}
                >
                    {submitting ? 'Sending' : 'Send message'}
                    {submitting
                        ? <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                        : <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />}
                </button>

                <p id='contact-note' className='max-w-xs text-xs leading-relaxed text-muted-foreground'>
                    We only use your details to answer this request. No newsletters, ever.
                </p>
            </div>
        </form>
        </>
    )
}
